/**
 * Contracts for Beacon → client WebRTC e2e harness (PHASER beacon-client-webrtc-e2e).
 * Shapes and thresholds only — no runtime wiring.
 */
import { DESKTOP_MAX_BITRATE_BPS, RECOVERY_MAX_BITRATE_BPS } from './webrtcLatencyContracts';

/** Env / Vite flag: expose window.__playcastBeaconE2e only when truthy ("1" / "true"). */
export const BEACON_WEBRTC_E2E_HOOKS_ENV = 'BEACON_WEBRTC_E2E_HOOKS' as const;

export const PLAYCAST_BEACON_E2E_WINDOW_KEY = '__playcastBeaconE2e' as const;

/** Host insertable impairment bridge (product-path adverse suite). Same env gate. */
export const PLAYCAST_BEACON_IMPAIR_WINDOW_KEY = '__playcastBeaconImpair' as const;

export type BeaconE2eCongestionLevel = 'elevated' | 'critical';

export type BeaconE2eRole = 'host' | 'client';

export type BeaconE2eRecoveryState = 'normal' | 'recovering' | 'escalated';

export type BeaconE2eSnapshot = {
  role: BeaconE2eRole;
  rttMs?: number;
  packetsLostPct?: number;
  maxBitrateBps?: number;
  recoveryState?: BeaconE2eRecoveryState;
  idrRequestCount?: number;
  lastCongestionSent?: { level: BeaconE2eCongestionLevel; atMs: number };
  /** Host only — last applied local answer SDP after transforms. */
  lastAnswerSdp?: string;
};

export type PlaycastBeaconE2eApi = {
  getSnapshot(): Promise<BeaconE2eSnapshot> | BeaconE2eSnapshot;
  /** Client only — force send congestion on datachannel. */
  injectCongestion?(level: BeaconE2eCongestionLevel): void;
};

export const BEACON_E2E_SCENARIO_IDS = {
  JOIN_QUALITY: 'join-quality',
  RECONNECT_SAME_SESSION: 'reconnect-same-session',
  ANSWER_TIAS_AS: 'answer-tias-as',
  CONGESTION_RECOVERY: 'congestion-recovery',
} as const;

/** Expected answer bandwidth lines for desktop 8 Mbps max. */
export const BEACON_E2E_EXPECTED_TIAS_BPS = DESKTOP_MAX_BITRATE_BPS;
export const BEACON_E2E_EXPECTED_AS_KBPS = Math.round(DESKTOP_MAX_BITRATE_BPS / 1000);

/** Loose LAN RTT band (ms) after settle. */
export const BEACON_E2E_RTT_MS_MAX_LAN = 80;
export const BEACON_E2E_LOSS_PCT_MAX = 1;

/** Settle / sample windows. */
export const BEACON_E2E_SETTLE_MS_LOCAL = 15_000;
export const BEACON_E2E_SETTLE_MS_CI = 8_000;
export const BEACON_E2E_JOIN_SAMPLE_MS_LOCAL = 60_000;
export const BEACON_E2E_JOIN_SAMPLE_MS_CI = 20_000;

export const BEACON_E2E_RECOVERY_MAX_BITRATE_BPS = RECOVERY_MAX_BITRATE_BPS;

export function isBeaconE2eHooksEnabled(envValue: string | undefined): boolean {
  if (envValue === undefined || envValue === null) return false;
  const v = String(envValue).trim().toLowerCase();
  return v === '1' || v === 'true';
}

export function isBeaconE2eCongestionLevel(
  value: unknown,
): value is BeaconE2eCongestionLevel {
  return value === 'elevated' || value === 'critical';
}

export function isBeaconE2eSnapshot(value: unknown): value is BeaconE2eSnapshot {
  if (value === null || typeof value !== 'object') return false;
  const s = value as Record<string, unknown>;
  if (s['role'] !== 'host' && s['role'] !== 'client') return false;
  if (s['rttMs'] !== undefined && typeof s['rttMs'] !== 'number') return false;
  if (s['packetsLostPct'] !== undefined && typeof s['packetsLostPct'] !== 'number') {
    return false;
  }
  if (s['maxBitrateBps'] !== undefined && typeof s['maxBitrateBps'] !== 'number') {
    return false;
  }
  if (
    s['recoveryState'] !== undefined &&
    s['recoveryState'] !== 'normal' &&
    s['recoveryState'] !== 'recovering' &&
    s['recoveryState'] !== 'escalated'
  ) {
    return false;
  }
  if (
    s['idrRequestCount'] !== undefined &&
    typeof s['idrRequestCount'] !== 'number'
  ) {
    return false;
  }
  if (s['lastCongestionSent'] !== undefined) {
    const last = s['lastCongestionSent'];
    if (last === null || typeof last !== 'object') return false;
    const lc = last as Record<string, unknown>;
    if (!isBeaconE2eCongestionLevel(lc['level'])) return false;
    if (typeof lc['atMs'] !== 'number') return false;
  }
  if (s['lastAnswerSdp'] !== undefined && typeof s['lastAnswerSdp'] !== 'string') {
    return false;
  }
  return true;
}
